import { useEffect, useMemo, useRef } from 'react';

import { InceptHeader } from '~/core/shared/api/constants/InceptHeader';
import { useAuthApi } from '~/core/shared/contexts/AuthContext';
import { useUserData } from '~/core/shared/contexts/UserContext';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Nullable } from '../../../../types';
import { createEcho } from '../../logic';
import {
  PrivateEchoPresenceWebsocketService,
  PrivateEchoWebsocketService,
  PublicEchoWebsocketService,
} from '../../services';
import { WebsocketContextValue } from '../../WebsocketContext';

type WebsocketServices = {
  publicWebsocketService: PublicEchoWebsocketService;
  privateWebsocketService: PrivateEchoWebsocketService;
  privatePresenceWebsocketService: PrivateEchoPresenceWebsocketService;
};

/**
 * Owns the stable service instances AND the connection lifecycle.
 *
 * The services exist for the life of the provider regardless of connection
 * state, so consumer hooks never crash on mount and subscriptions made before a
 * connection exists are carried over once one is supplied.
 *
 * The connection is keyed on the authenticated user's id: it is created once the
 * user is known, recreated when the user changes (a new Ably clientId, which is
 * immutable per connection, so a fresh instance is required to avoid Ably 40102),
 * and closed on logout. JWT rotation does NOT recreate the connection; the
 * current token is read per auth request via the dynamic auth headers.
 *
 * This hook must run below both AuthProvider and UserProvider.
 */
export const useWebsocketManagement = (): WebsocketContextValue => {
  const servicesRef = useRef<Nullable<WebsocketServices>>(null);

  if (!servicesRef.current) {
    servicesRef.current = {
      publicWebsocketService: new PublicEchoWebsocketService(),
      privateWebsocketService: new PrivateEchoWebsocketService(),
      privatePresenceWebsocketService:
        new PrivateEchoPresenceWebsocketService(),
    };
  }

  const services = servicesRef.current;

  const { user } = useUserData();
  const { getAccessToken, getTwoFactorToken } = useAuthApi();
  const userId = user?.id ?? null;

  // Stable getter that reads the current tokens each time it is called, so a
  // rotated JWT is picked up by the connection's next auth request.
  const getAuthHeaders = useEventHandler((): Record<string, string> => {
    const accessToken = getAccessToken();
    const twoFactorToken = getTwoFactorToken();

    return {
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...(twoFactorToken && { [InceptHeader.TwoFactorToken]: twoFactorToken }),
    };
  });

  const connection = useMemo(
    () => (userId ? createEcho(getAuthHeaders) : null),
    [userId, getAuthHeaders],
  );

  useEffect(() => {
    services.publicWebsocketService.setConnection(connection);
    services.privateWebsocketService.setConnection(connection);
    services.privatePresenceWebsocketService.setConnection(connection);

    return () => {
      services.publicWebsocketService.setConnection(null);
      services.privateWebsocketService.setConnection(null);
      services.privatePresenceWebsocketService.setConnection(null);
      connection?.disconnect();
    };
  }, [connection, services]);

  return useMemo(() => ({ ...services, connection }), [services, connection]);
};
